/**
 * Practice runner - quiz path.
 *
 * Walks the practice queue concept-by-concept. For each concept the
 * payload carries 1-2 MCQ questions. The user picks an option and hits
 * Submit; correctness is revealed inline and the answer is auto-graded
 * (no second self-rate step). After all questions for a concept, the
 * per-question verdicts roll up into a single FSRS grade:
 *
 *   100% correct → 4 (Easy)
 *   ≥50% correct → 3 (Good)
 *    >0% correct → 2 (Hard)
 *      0 correct → 1 (Again)
 *
 * The grade goes through `useSubmitReview`. Per-concept `outcomes`
 * (with full `attempts` for the audit view) bubble up via `onEnd` /
 * `onProgress` so the orchestrator can record them in the session log.
 *
 * Owns its own chrome (RunnerTopBar) so it's a structural peer of
 * FlashcardRunner - the parent shell just feeds it the queue.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { notify } from '@/lib/notify';
import { Button } from '@ai-tutor/ui';
import {
  BlockMarkdown,
  InlineMarkdown,
} from '@/components/core/markdown/markdown';
import type { MindspaceConcept } from '@/lib/api-client';
import { VisualBlock } from '@/components/core/visual';
import type { Visual } from '@/types/visual';
import { useConceptStars } from '@/hooks/mindspace/useConceptStars';
import { useSubmitReview } from '@/hooks/mindspace/useRevision';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';
import {
  ConceptContextStrip,
  QUIZ_SHORTCUTS,
  useSessionStart,
} from '@/components/features/mindspace/practice/runners/RunnerChrome';
import { QuizActions } from '@/components/features/mindspace/practice/runners/QuizActions';
import { RunnerChromeFrame } from '@/components/features/mindspace/practice/runners/RunnerChromeFrame';
import {
  ComboBadge,
  nextCombo,
} from '@/components/features/mindspace/practice/widgets/ComboBadge';
import { StarButton } from '@/components/features/mindspace/practice/widgets/StarButton';
import {
  aggregateGradeFromAttempts,
  isCorrectAnswer,
  nextActionLabel,
} from '@/lib/practice/quizGrading';
import { isOnScreen, isTypingTarget } from './isOnScreen';
import type {
  QuestionAttempt,
  SessionOutcome,
} from '@/lib/practice/session-types';

interface PracticeRunnerProps {
  concepts: MindspaceConcept[];
  isLoading?: boolean;
  /** Called when the runner finishes the queue or the user ends early.
   *  Receives the accumulated per-concept outcomes (with attempts for
   *  the audit view) + the total concept count. */
  onEnd: (outcomes: SessionOutcome[], total: number) => void;
  /** Fires after each concept's grade is submitted so the orchestrator
   *  can snapshot the in-progress outcomes for a partial save on a
   *  mid-session switch or crash. `total` is the deck size, so that save
   *  records "1 of 3" rather than "1 of 1". */
  onProgress?: (outcomes: SessionOutcome[], total: number) => void;
  /** Bail-out callback used by the empty / error states - returns the
   *  user to the setup screen without recording a session. */
  onAbort?: () => void;
  /** Whether each concept's grade should submit a real FSRS review.
   *  True for Review scopes (Due / Weak); false for no-stakes Practice
   *  scopes (Starred / Recent / Custom) where drilling must not reschedule.
   *  Defaults to true. */
  recordReview?: boolean;
}

const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

/** Fisher-Yates shuffle (returns a new array). Used to randomize MCQ option
 *  order each session so a repeated quiz doesn't have the answer in the same
 *  spot - cheap anti-memorization. Correctness is compared by answer text,
 *  not index, so reordering is safe. */
function shuffle<T>(arr: readonly T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export function PracticeRunner({
  concepts,
  isLoading,
  onEnd,
  onProgress,
  onAbort,
  recordReview = true,
}: PracticeRunnerProps) {
  const submit = useSubmitReview();
  const phone = useMediaQuery('(max-width: 767.98px)');
  const [conceptIndex, setConceptIndex] = useState(0);
  const [questionIndex, setQuestionIndex] = useState(0);
  // Per-concept attempts accumulated so far (resets each concept).
  const [conceptAttempts, setConceptAttempts] = useState<QuestionAttempt[]>([]);
  // All concept-level outcomes accumulated for the whole session.
  const [outcomes, setOutcomes] = useState<SessionOutcome[]>([]);
  // Index of the currently-picked option (null = nothing picked).
  const [pickedIndex, setPickedIndex] = useState<number | null>(null);
  // Submit flips this; correctness + explanation render below.
  const [submitted, setSubmitted] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);
  const [finishedAll, setFinishedAll] = useState(false);
  // Consecutive correct-answer streak across questions within the
  // session. Resets on a wrong answer. Drives the ComboBadge cue.
  const [comboLength, setComboLength] = useState(0);
  const [comboBumpKey, setComboBumpKey] = useState(0);

  // This copy's root, for the visible-instance shortcut gate.
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Session clock. Starts once the deck is ready; the tick lives in
  // `SessionTimer` so it re-renders only the chip, never this card.
  const ready = !isLoading && concepts.length > 0;
  const startedAt = useSessionStart(ready);

  const concept = concepts[conceptIndex];
  // Same per-concept star state as the flashcard runner: the deck is frozen,
  // so the payload's `is_bookmarked` never updates under a starred concept.
  const stars = useConceptStars();
  const questions = concept?.questions ?? [];
  const question = questions[questionIndex];
  const totalConcepts = concepts.length;

  // MCQ options in shuffled display order, stable for the lifetime of this
  // question (re-shuffles only when the question changes). `pickedIndex`,
  // the rendered list, and the answer extraction all index into THIS array.
  // Quiz is MCQ/true-false only - every question carries options.
  const displayOptions = useMemo(
    () => (question?.options ? shuffle(question.options) : []),
    [question]
  );

  const canSubmit = pickedIndex != null;

  const submitCurrent = useCallback(() => {
    if (!concept || !question || submitted || pickedIndex == null) return;
    const answer = displayOptions[pickedIndex];
    const wasCorrect = isCorrectAnswer(answer, question.correct_answer);
    setComboLength(prev => nextCombo(prev, wasCorrect));
    setComboBumpKey(k => k + 1);
    setSubmitted(true);
    // Pre-open the explanation panel - it's the highest-value
    // pedagogical content; hiding it behind a click depresses
    // engagement. The user can dismiss with ✕ or Next.
    setWhyOpen(true);
  }, [concept, question, submitted, pickedIndex, displayOptions]);

  const advance = useCallback(() => {
    if (!concept || !question) return;
    const pickedAnswer =
      pickedIndex != null ? displayOptions[pickedIndex] : undefined;
    const wasCorrect = isCorrectAnswer(pickedAnswer, question.correct_answer);

    const attempt: QuestionAttempt = {
      question: question.question,
      correctAnswer: question.correct_answer,
      explanation: question.explanation,
      // Record the order the user actually saw (audit view).
      options: displayOptions,
      pickedAnswer,
      wasCorrect,
    };
    const nextAttempts = [...conceptAttempts, attempt];

    const moreQuestions = questionIndex + 1 < questions.length;
    if (moreQuestions) {
      setConceptAttempts(nextAttempts);
      setQuestionIndex(i => i + 1);
      setSubmitted(false);
      setWhyOpen(false);
      setPickedIndex(null);
      return;
    }

    // End of this concept - aggregate the grade. Submit an FSRS review only
    // for Review scopes; no-stakes Practice scopes drill without rescheduling
    // (`recordReview=false`). Fire-and-forget: the idempotency token guards
    // double-submit, so blocking UI advance on the round-trip just slows nav.
    const grade = aggregateGradeFromAttempts(nextAttempts);
    if (recordReview) {
      submit.mutate(
        {
          conceptId: concept.concept_id,
          grade,
          source: 'practice',
          attemptId: crypto.randomUUID(),
        },
        {
          onError: (err: any) => {
            notify.error(err, "Couldn't record review");
          },
        }
      );
    }

    const outcome: SessionOutcome = {
      conceptId: concept.concept_id,
      conceptName: concept.name,
      grade,
      retention: concept.retention_now,
      attempts: nextAttempts,
    };
    const nextOutcomes = [...outcomes, outcome];
    setOutcomes(nextOutcomes);
    onProgress?.(nextOutcomes, totalConcepts);

    const nextConceptIndex = conceptIndex + 1;
    if (nextConceptIndex >= totalConcepts) {
      setFinishedAll(true);
      onEnd(nextOutcomes, totalConcepts);
      return;
    }

    // Advance straight to the next concept's Q1 - no transition card.
    setConceptIndex(nextConceptIndex);
    setQuestionIndex(0);
    setConceptAttempts([]);
    setSubmitted(false);
    setWhyOpen(false);
    setPickedIndex(null);
  }, [
    concept,
    question,
    pickedIndex,
    displayOptions,
    conceptAttempts,
    questionIndex,
    questions.length,
    conceptIndex,
    totalConcepts,
    submit,
    recordReview,
    outcomes,
    onProgress,
    onEnd,
  ]);

  const correctSoFar = useMemo(
    () => conceptAttempts.filter(a => a.wasCorrect).length,
    [conceptAttempts]
  );

  // Keyboard: A-D / 1-4 pick, Enter submits or advances, W toggles Why.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Visible copy only. `MindspaceLayout` mounts the runner twice, so an
      // ungated window listener runs every shortcut in both — the flashcard
      // runner was submitting two FSRS reviews per key press for exactly this
      // reason. Here it would double-submit and double-advance.
      if (!isOnScreen(rootRef.current)) return;
      if (isTypingTarget(e)) return;
      if (!concept || !question) return;

      // Pick option via 1-4 or A-D.
      const num = ['1', '2', '3', '4'].indexOf(e.key);
      const letter = OPTION_LETTERS.indexOf(e.key.toUpperCase());
      const pickIndex = num >= 0 ? num : letter >= 0 ? letter : -1;
      if (pickIndex >= 0 && pickIndex < question.options.length && !submitted) {
        e.preventDefault();
        setPickedIndex(pickIndex);
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        if (!submitted) {
          submitCurrent();
        } else {
          void advance();
        }
        return;
      }

      if ((e.key === 'w' || e.key === 'W') && submitted) {
        e.preventDefault();
        setWhyOpen(o => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [concept, question, pickedIndex, submitted, advance, submitCurrent]);

  if (isLoading) {
    return (
      <div className='flex h-full min-h-[300px] flex-col items-center justify-center gap-3 py-16'>
        <Loader2 className='h-6 w-6 animate-spin text-brand-600 dark:text-primary' />
        <p className='text-sm text-muted-foreground'>
          Generating practice questions…
        </p>
      </div>
    );
  }

  if (totalConcepts === 0) {
    return (
      <div className='flex h-full min-h-[300px] flex-col items-center justify-center gap-3 py-16 text-center'>
        <p className='text-sm font-medium text-foreground'>
          Nothing to practice in this scope.
        </p>
        {onAbort && (
          <Button variant='outline' onClick={onAbort}>
            Back to setup
          </Button>
        )}
      </div>
    );
  }

  if (finishedAll) {
    // Parent handles the results screen. This render is a brief moment
    // while the orchestrator transitions phases.
    return null;
  }

  if (!concept || !question) return null;

  const actions = (
    <QuizActions
      pinned={phone}
      submitted={submitted}
      canSubmit={canSubmit}
      onSubmit={submitCurrent}
      onNext={advance}
      nextLabel={nextActionLabel({
        questionIndex,
        questionCount: questions.length,
        conceptIndex,
        conceptCount: totalConcepts,
      })}
    />
  );

  return (
    <QuizChrome
      footer={phone ? actions : undefined}
      onEnd={() => onEnd(outcomes, totalConcepts)}
      startedAt={startedAt}
      index={conceptIndex}
      total={totalConcepts}
      momentum={<ComboBadge comboLength={comboLength} bumpKey={comboBumpKey} />}
    >
      <div
        ref={rootRef}
        className='flex min-h-0 flex-1 flex-col items-center overflow-y-auto px-6 py-12 max-md:px-4 max-md:py-4'
      >
        <div
          key={`${concept.concept_id}-${questionIndex}`}
          className='flex w-full max-w-2xl flex-col gap-7 max-md:gap-5'
        >
          {/* Inline concept-context strip - replaces the older (i)
                toggle so the metadata is at-a-glance. */}
          <ConceptContextStrip
            domain={concept.domain_tags?.[0]}
            level={concept.level}
            lastReviewedAt={concept.sr_state.last_reviewed_at}
            retention={concept.retention_now}
          />

          {/* Question */}
          <section aria-live='polite'>
            <div className='mb-3 flex items-center justify-between gap-3'>
              <div className='flex items-center gap-3 text-xs font-semibold text-muted-foreground'>
                <span>Question</span>
                {questions.length > 1 && (
                  <QuestionPillProgress
                    index={questionIndex}
                    total={questions.length}
                    correctSoFar={correctSoFar}
                    submittedSoFar={conceptAttempts.length}
                  />
                )}
              </div>
              <StarButton
                starred={stars.isStarred(concept)}
                onToggle={() => stars.toggle(concept)}
              />
            </div>
            <BlockMarkdown className='text-lg font-medium leading-relaxed text-foreground sm:text-xl'>
              {question.question}
            </BlockMarkdown>
          </section>

          <McqOptions
            options={displayOptions}
            correctAnswer={question.correct_answer}
            submitted={submitted}
            pickedIndex={pickedIndex}
            onPick={i => !submitted && setPickedIndex(i)}
          />

          {submitted &&
            (question.explanation || question.explanation_visual) &&
            whyOpen && (
              <ExplanationPanel
                explanation={question.explanation}
                visual={question.explanation_visual ?? undefined}
                wasCorrect={
                  pickedIndex != null &&
                  isCorrectAnswer(
                    displayOptions[pickedIndex],
                    question.correct_answer
                  )
                }
              />
            )}

          {/* Action row - fades in once an option is picked. Submit then
                Next share the same button slot so the click target is
                consistent. On a phone it is the chrome's footer instead. */}
          {!phone && (
            <div className='flex h-12 items-center justify-center'>
              {actions}
            </div>
          )}
        </div>
      </div>
    </QuizChrome>
  );
}

/**
 * Quiz chrome - mirrors `FlashcardChrome` exactly. Both surfaces feel
 * like siblings because they share the floating-element shell instead
 * of a top banner.
 */
function QuizChrome({
  onEnd,
  startedAt,
  index,
  total,
  momentum,
  footer,
  children,
}: {
  onEnd: () => void;
  startedAt: number | null;
  index: number;
  total: number;
  momentum?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <RunnerChromeFrame
      onEnd={onEnd}
      startedAt={startedAt}
      index={index}
      total={total}
      momentum={momentum}
      shortcuts={QUIZ_SHORTCUTS}
      footer={footer}
    >
      {children}
    </RunnerChromeFrame>
  );
}

function QuestionPillProgress({
  index,
  total,
  correctSoFar,
  submittedSoFar,
}: {
  index: number;
  total: number;
  correctSoFar: number;
  submittedSoFar: number;
}) {
  return (
    <span className='flex items-center gap-1.5 text-xs normal-case tracking-normal text-muted-foreground'>
      {Array.from({ length: total }).map((_, i) => {
        const isSubmitted = i < submittedSoFar;
        // We only know correctness for submitted ones; treat the rest
        // as neutral filled-on-current, hollow-otherwise.
        const isCorrect = isSubmitted && i < correctSoFar; // approx - correct ones are first by construction
        const tone = isSubmitted
          ? isCorrect
            ? 'bg-emerald-500'
            : 'bg-rose-400'
          : i === index
            ? 'bg-brand-500'
            : 'border border-border';
        return (
          <span key={i} className={`block h-1.5 w-1.5 rounded-full ${tone}`} />
        );
      })}
      <span className='ml-1 opacity-70'>
        Question {index + 1} of {total}
      </span>
    </span>
  );
}

function McqOptions({
  options,
  correctAnswer,
  submitted,
  pickedIndex,
  onPick,
}: {
  options: string[];
  correctAnswer: string;
  submitted: boolean;
  pickedIndex: number | null;
  onPick: (i: number) => void;
}) {
  return (
    // A RADIOGROUP, not a plain list of buttons. These are single-choice —
    // picking one unpicks the others — and as bare buttons nothing announced
    // which was selected: a screen-reader user could hear four options, press
    // one, and get no confirmation that anything had happened. The same
    // finding as the practice mode toggle in Phase 10, in a place where the
    // whole task is making a choice.
    //
    // `role='none'` on the `li` because a radiogroup's children must be
    // radios; the list semantics are not doing any work here anyway.
    <ul role='radiogroup' aria-label='Answer options' className='space-y-2'>
      {options.map((option, i) => {
        const letter = OPTION_LETTERS[i] ?? String(i + 1);
        const isCorrect = isCorrectAnswer(option, correctAnswer);
        const isPicked = pickedIndex === i;

        // Color logic:
        //   - Not submitted: picked = indigo border, idle = subtle
        //   - Submitted: correct = emerald, picked-but-wrong = rose,
        //                others = muted (no opacity - keep readable)
        // Submitted correct/wrong colours use the SAME semantic success/
        // destructive tokens as the post-session results audit
        // (OutcomeAuditList) so the same state reads identically in both
        // places instead of a slightly different red/green.
        const colorCls = submitted
          ? isCorrect
            ? 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-success/30 dark:bg-success/20 dark:text-emerald-100'
            : isPicked
              ? 'border-rose-200 bg-rose-50 text-rose-900 dark:border-destructive/30 dark:bg-destructive/20 dark:text-rose-100'
              : 'border-border bg-card text-muted-foreground'
          : isPicked
            ? 'border-brand-200 bg-brand-50 text-brand-900 dark:border-primary/50 dark:tint-brand'
            : 'border-border bg-card text-foreground hover:border-gray-300 hover:bg-accent';

        const letterChipCls = submitted
          ? isCorrect
            ? 'bg-emerald-100 text-emerald-700 dark:bg-success/20 dark:text-success'
            : isPicked
              ? 'bg-rose-100 text-rose-700 dark:bg-destructive/20 dark:text-destructive-emphasis'
              : 'bg-muted text-muted-foreground'
          : isPicked
            ? 'bg-brand-100 text-brand-700 dark:tint-brand-strong'
            : 'bg-muted text-muted-foreground';

        return (
          <li key={`${i}-${option}`} role='none'>
            <button
              type='button'
              role='radio'
              aria-checked={isPicked}
              onClick={() => onPick(i)}
              disabled={submitted}
              className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left text-base transition-colors disabled:cursor-default ${colorCls}`}
            >
              <span
                className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md font-mono text-xs font-semibold transition-colors ${letterChipCls}`}
              >
                {letter}
              </span>
              <span className='flex-1'>
                <InlineMarkdown>{option}</InlineMarkdown>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Brilliant-style slide-up explanation. Renders pre-expanded the moment
 * the user submits - explanation is the highest-value pedagogical
 * content, and hiding it behind a click depresses engagement. Tone
 * follows the user's correctness so outcome + reasoning read as a single
 * visual unit.
 */
function ExplanationPanel({
  explanation,
  visual,
  wasCorrect,
}: {
  explanation: string;
  /** The concept's picture or diagram: after the answer, so it gives nothing
   *  away. */
  visual?: Visual;
  wasCorrect: boolean;
}) {
  const accent = wasCorrect
    ? 'border-l-emerald-400 dark:border-l-emerald-600'
    : 'border-l-rose-400 dark:border-l-rose-600';

  return (
    <div
      role='note'
      aria-live='polite'
      className={`rounded-lg border border-l-4 border-border bg-card px-4 py-3 motion-safe:duration-200 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 ${accent}`}
    >
      <div className='mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
        <span>Why?</span>
        <kbd className='hidden font-mono text-xs normal-case tracking-normal text-muted-foreground sm:inline'>
          W
        </kbd>
      </div>
      {explanation && (
        <BlockMarkdown className='text-sm leading-relaxed text-foreground'>
          {explanation}
        </BlockMarkdown>
      )}
      {visual && (
        <VisualBlock visual={visual} compact caption={false} className='mt-3' />
      )}
    </div>
  );
}
